import { captureEvent, useCaptureEvent } from "@Light/app/analytics";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import type { Utility } from "@Light/services/lightTypes";
import { useState } from "react";
import { EnrollBottomSheet } from "../common/EnrollBottomSheet";
import { EnrollHelpButton } from "../EnrollHelpButtonFooter";
import {
  makeUtilityChoiceSubtitle,
  OTHER_UTILITY,
  utilityChoiceHelpButtonText,
  UtilityRadioGroup,
  type UtilityChoice,
} from "./UtilityRadioGroup";

export type ZipUtilitySplitBottomSheetProps = {
  open: boolean;
  onClose: () => void;
  postalCode?: string;
  utilities: Utility[];
  onSubmit: (utility: Utility) => void;
  onSelectOtherUtility: () => void;
  onClickProvideAddress: () => void;
};

export const ZipUtilitySplitBottomSheet = ({
  open,
  onClose,
  postalCode,
  utilities,
  onSubmit,
  onSelectOtherUtility,
  onClickProvideAddress,
}: ZipUtilitySplitBottomSheetProps) => {
  const [selected, setSelected] = useState<UtilityChoice>();

  useCaptureEvent({
    event: "enroll_zip_split_viewed",
    condition: open,
    properties: {
      variant: "sheet",
      postal_code: postalCode ?? null,
      utility_count: utilities.length,
    },
  });

  return (
    <EnrollBottomSheet
      open={open}
      onClose={() => {
        captureEvent("enroll_zip_split_dismissed", {
          variant: "sheet",
          postal_code: postalCode ?? null,
        });
        setSelected(undefined);
        onClose();
      }}
      title="Who is your local utility provider?"
      subtitle={makeUtilityChoiceSubtitle(postalCode, utilities)}
      data-testid="zip-utility-split-sheet"
      footer={
        <ButtonFooter>
          <Button
            size="lg"
            className="w-full"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              const isOther = selected === OTHER_UTILITY;
              captureEvent("enroll_zip_split_submitted", {
                variant: "sheet",
                postal_code: postalCode ?? null,
                utility: isOther ? OTHER_UTILITY : selected.id,
              });
              if (isOther) {
                setSelected(undefined);
                onSelectOtherUtility();
              } else {
                onSubmit(selected);
              }
            }}
            data-testid="zip-utility-split-sheet-submit"
          >
            View offer
          </Button>
          <EnrollHelpButton eventVariant="split_utility_sheet">
            {utilityChoiceHelpButtonText(utilities)}
          </EnrollHelpButton>
        </ButtonFooter>
      }
    >
      <UtilityRadioGroup
        name="zip-utility-split-sheet"
        utilities={utilities}
        value={selected}
        onChange={setSelected}
        onClickProvideAddress={onClickProvideAddress}
      />
    </EnrollBottomSheet>
  );
};
